import React, {useMemo, useRef, useState} from "react";
import {createUUId, readFile, ReadingFileAs} from "../../../../../core/services/utils/utils";
import LazyImage from "../../lazy-image";
import {Col, Row} from "reactstrap";
import {ReactComponent as ImagePlaceholder} from "../../../../../assets/images/dropzone/image-placeholder.svg";
import DropZone from "../../../base/dropzone";
import {InputFileAcceptTypes, UploadStates} from "../../../../../core/constants/enums";
import Button from "../../../base/button/button";
import {SvgIcon} from "@mui/material";
import {ReactComponent as RemoveIcon} from "../../../../../assets/images/remove.svg";
import classnames from "classnames";
import ToastService from "../../../../../core/services/toast";

const ManageTagIconDropzone = ({
                                   accept = [
                                       InputFileAcceptTypes.png,
                                       InputFileAcceptTypes.svg,
                                   ],
                                   upload,
                                   uploadState,
                                   remove,
                                   removing,
                                   url,
                               }) => {
    const [loading, setLoading] = useState(false);
    const [tempUrl, setTempUrl] = useState();
    const idRef = useRef(createUUId(true));

    const uploading = useMemo(() => uploadState?.state === UploadStates.pending, [uploadState]);
    const disabled = useMemo(() => uploading, [uploading]);

    /**
     * Uploads the file content based on its extension.
     * * if it's an image, uploads it right away
     * * if it's a svg, first determines if the svg has an embed image inside or not, then uploads it's content.
     * @param files {File[] | null}
     */
    const onFileSelected = async (files) => {
        const file = files[0];
        if (file.name.toLowerCase().endsWith('.svg')) {
            return await processSelectedSvg(file);
        } else {
            return await uploadImage(file);
        }
    }

    /**
     * Invokes the upload callback with the contentBytes of the given image file and its name.
     * @param {File} file
     * @return {Promise<void>}
     */
    const uploadImage = async (file) => {
        const dataUrl = await readFile(file, ReadingFileAs.dataUrl);
        setTempUrl(dataUrl);
        upload({
            contentBytes: btoa(await readFile(file, ReadingFileAs.binaryString)),
            dataUrl: dataUrl,
            fileName: file.name,
        })
    }

    /**
     * Invokes the upload callback with the contentBytes of the given svg file and its name.
     *
     * * if the svg contains an image inside it, uploads the image instead of the whole svg (to prevent the).
     * * injects 'static-svg' classname to the svg tag prior to uploading it
     * @param {File} file
     * @return {Promise<any>}
     */
    const processSelectedSvg = async (file) => {
        const parentEle = document.createElement('div');
        const svgString = await fetchSvg(await readFile(file, ReadingFileAs.dataUrl));
        if (!svgString) {
            return setTempUrl(await readFile(file, ReadingFileAs.dataUrl));
        }

        // inject static-svg class to the svg tag.
        parentEle.innerHTML = svgString;
        const innerImage = parentEle.querySelector('image');

        if (!innerImage) {
            const svgEle = parentEle.querySelector('svg');
            return await uploadSvg(file, svgEle);
        }

        // the inner image's href is a link and not base64
        const hrefVal = innerImage.href.animVal.toLowerCase();
        if (
            (hrefVal.startsWith('https://') && !hrefVal.endsWith('.png')) ||
            (!hrefVal.startsWith('https://') && !hrefVal.startsWith('data:image/png'))
        ) {
            return ToastService.error({
                title: "Invalid SVG content",
                message: "The selected SVG file contains an image that is not of type PNG, please edit the file" +
                    " prior to uploading it.",
                hideTicketSubmission: true,
            })
        }

        // uploading the image with base 64.
        return await uploadSVGsInnerImage(file, innerImage);
    }

    /**
     * Uploads the svg element after injecting static-svg class name to it.
     * @param file
     * @param svgEle
     * @return {Promise<void>}
     */
    const uploadSvg = async (file, svgEle) => {
        svgEle.classList.add('static-svg');
        // create a new file object from the updated svg tag.
        const editedFile = new Blob([svgEle.outerHTML], {type: "image/svg+xml;charset=utf-8"})

        const dataUrl = await readFile(editedFile, ReadingFileAs.dataUrl);
        setTempUrl(dataUrl)
        upload({
            contentBytes: btoa(await readFile(editedFile, ReadingFileAs.binaryString)),
            dataUrl: dataUrl,
            fileName: file.name,
        })
    }

    /**
     * Uploads the SVG's inner image.
     * @param file
     * @param innerImage
     * @return {Promise<*>}
     */
    const uploadSVGsInnerImage = async (file, innerImage) => {
        // uploading the image with link src.
        const innerImageObj = new Image();
        innerImageObj.src = innerImage.href.animVal;
        const editedFile = await fetch(innerImageObj.src).then(e => e.blob()).catch(() => null);

        const dataUrl = await readFile(editedFile, ReadingFileAs.dataUrl);
        setTempUrl(dataUrl)
        return upload({
            contentBytes: btoa(await readFile(editedFile, ReadingFileAs.binaryString)),
            dataUrl: dataUrl,
            fileName: file.name,
        })
    }

    /**
     * Loads the svg from the given url.
     * @return {Promise<string>}
     */
    const fetchSvg = async (url) => {
        try {
            const response = await fetch(url);
            if (response.status === 200)
                return await response.text();
        } catch (e) {
            return null;
        }
    }

    return (
        <>
            <DropZone
                id={idRef.current}
                disabled={disabled}
                identifier={'manage-tag-icon'}
                accept={accept}
                onSuccess={onFileSelected}
                multiple={false}
                loading={loading}
                uploading={uploading}
                setLoading={setLoading}
                openOnClick={!disabled}
                className={`d-flex align-items-center justify-content-center`}
                noParse>
                {() => uploading
                    ? <div
                        className={'pending-content-container'}>
                        <p className={classnames(
                            'progress-percentage',
                            {'red': uploadState.state === UploadStates.error,}
                        )}>
                            {
                                uploading
                                    ? ((uploadState.percentage ?? 0) + '%')
                                    : uploadState.state
                            }
                        </p>
                        {
                            tempUrl?.length > 0
                                ? <img
                                    alt={'uploading-preview'}
                                    className='tag-icon'
                                    src={tempUrl}
                                    style={{
                                        opacity: uploadState.state === UploadStates.done ? '1' : '0.5'
                                    }}
                                />
                                : <div className='background-grey-1 cursor-progress-hover rounded-xxl'
                                       style={{
                                           width: '100px',
                                           height: '100px',
                                           opacity: uploadState.state === UploadStates.done ? '1' : '0.5'
                                       }}
                                />
                        }
                    </div>
                    : url?.length > 0
                        ? <Row
                            className={`w-100 no-gutters align-items-center justify-content-center`}>
                            <Col xs={12} className={'d-flex justify-content-center align-items-center'}>
                                <LazyImage
                                    className='tag-icon cursor-pointer-hover'
                                    src={url}
                                />
                            </Col>
                            <Col xs={12} className={'d-flex flex-column justify-content-center align-items-center'}>
                                <p className={'placeholder-text'}>
                                    Click here or drag and drop your file <br/>
                                    your file should be in SVG or PNG format <br/>
                                    You can also ask us to design your icons
                                </p>
                            </Col>
                            <Col xs={12} className={'d-flex flex-column justify-content-center align-items-center' +
                                ' mt-1'}>
                                <Button
                                    disabled={loading || uploading || removing}
                                    onClick={(e) => e.preventDefault() || e.stopPropagation() || remove()}
                                    variant={'text'}
                                    color={'primary'}
                                    className='w-100 d-flex align-items-center justify-content-center'>
                                    <SvgIcon component={RemoveIcon} className='icon normal'/>
                                    <p>
                                        {
                                            removing
                                                ? "Removing..."
                                                : "Remove the current icon"
                                        }
                                    </p>
                                </Button>
                            </Col>
                        </Row>
                        : (
                            <Row
                                className={`w-100 no-gutters align-items-center justify-content-center ${uploading ? 'cursor-progress-hover' : ''} ${loading ? 'cursor-not-allowed-hover' : 'cursor-pointer-hover'}`}>
                                <Col xs={12} className={'d-flex justify-content-center align-items-center'}>
                                    <ImagePlaceholder className={'placeholder-image'}/>
                                </Col>
                                <Col xs={12} className={'d-flex flex-column justify-content-center align-items-center'}>
                                    <p className={'placeholder-text'}>
                                        Click here or drag and drop your file <br/>
                                        your file should be in SVG or PNG format <br/>
                                        You can also ask us to design your icons
                                    </p>
                                </Col>
                            </Row>
                        )
                }
            </DropZone>
        </>
    )
}

export default ManageTagIconDropzone;
